import { useEffect, useState } from "react";
import { apiService } from '../../../components/Config/api.service';
import { FaTrashAlt, FaPlus, FaTag } from "react-icons/fa";
import Select from "react-select";
import EmployeeSelect from "../../../components/Formulaire/EmployeeSelect";
import { notify } from "../../../components/Utils/Notifications";
import { Modal } from "../../../components/Utils/ModalShow";
import AmountInput from "../../../components/Utils/AmountInput";

const TagModalContent = ({ onConfirm, onClose, type = "prime" }) => {
    const [selectedTags, setSelectedTags] = useState([]);
    const [primeAmount, setPrimeAmount] = useState('');
    const [tagOptions, setTagOptions] = useState([]);

    // Adapter le texte selon le type
    const entityName = type === "advantage" ? "avantage" : "prime";

    useEffect(() => {
        apiService.getElement('/employee/categories')
            .then(response => {
                const options = response.data.categories.map(cat => ({ value: cat.id, label: cat.name }));
                setTagOptions(options);
            })
            .catch(() => notify("Erreur lors du chargement des étiquettes.", '', 3));
    }, []);

    const handleConfirm = () => {
        if (selectedTags.length > 0 && primeAmount) {
            onConfirm(selectedTags.map(t => t.value), parseFloat(primeAmount));
            onClose();
        } else {
            notify(`Veuillez sélectionner au moins une étiquette et saisir un montant pour l'${entityName}.`, '', 3);
        }
    };

    return (
        <div className="space-y-6">
            <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700">Étiquettes</label>
                <Select
                    isMulti
                    options={tagOptions}
                    onChange={setSelectedTags}
                    placeholder="Sélectionner des étiquettes..."
                    className="react-select-container"
                    classNamePrefix="react-select"
                />
            </div>
            <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700">Montant de l'{entityName}</label>
                <input
                    type="number"
                    placeholder="Montant"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-colors duration-200"
                    value={primeAmount}
                    onChange={(e) => setPrimeAmount(e.target.value)}
                    step="0.01"
                    min="0"
                />
            </div>
            <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
                <button 
                    type="button" 
                    onClick={onClose} 
                    className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors duration-200"
                >
                    Annuler
                </button>
                <button 
                    type="button" 
                    onClick={handleConfirm} 
                    className="px-4 py-2 bg-indigo-600 text-white hover:bg-indigo-700 rounded-lg transition-colors duration-200"
                >
                    Confirmer
                </button>
            </div>
        </div>
    );
};

const EmployeesPrime = ({ 
    primeId, 
    onEmployeesPrimesChange, 
    saveChanges, 
    apiEndpoint = "/prime", 
    type = "prime" 
}) => { 
    const [employeesPrimes, setEmployeesPrimes] = useState([]);
    const [modifiedEmployeesPrimes, setModifiedEmployeesPrimes] = useState([]);
    const [newEmployeesPrimes, setNewEmployeesPrimes] = useState([]);
    const [removedEmployeesPrimes, setRemovedEmployeesPrimes] = useState([]);
    const [pagination, setPagination] = useState({
        offset: 0,
        limit: 15,
        total_count: 0,
        total_pages: 0
    });
    const [errorMessage, setErrorMessage] = useState('');
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isTagModalOpen, setIsTagModalOpen] = useState(false);
    const [nextPageOffset, setNextPageOffset] = useState(null);

    // Adapter les textes selon le type
    const entityName = type === "advantage" ? "avantage" : "prime";
    const entityNamePlural = type === "advantage" ? "avantages" : "primes";

    useEffect(() => {
        if (primeId) {
            fetchEmployeesPrimes();
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [primeId, pagination.offset, pagination.limit]);

    useEffect(() => {
        if (onEmployeesPrimesChange) {
            onEmployeesPrimesChange({
                employeesPrimes,
                newEmployeesPrimes,
                modifiedEmployeesPrimes,
                removedEmployeesPrimes
            });
        }
    }, [employeesPrimes, newEmployeesPrimes, modifiedEmployeesPrimes, removedEmployeesPrimes, onEmployeesPrimesChange]);

    const fetchEmployeesPrimes = async () => {
        if (!primeId) return;
        try {
            // Utiliser l'endpoint approprié selon le type
            const response = await apiService.getElement(`${apiEndpoint}/${primeId}/employees?offset=${pagination.offset}&limit=${pagination.limit}`);
            if (response.data.employees) {
                setEmployeesPrimes(response.data.employees);
                setPagination(response.data.pagination)
                setNewEmployeesPrimes([]);
                setModifiedEmployeesPrimes([]);
                setRemovedEmployeesPrimes([]);
            } else {
                setErrorMessage(`Failed to fetch employees ${entityNamePlural}.`);
            }
        } catch (error) {
            setErrorMessage(`Error fetching employees ${entityNamePlural}.`);
        }
    };

    const handleAddByTag = async (tagIds, amount) => {
        try {
            const response = await apiService.getElement(`/employees?filter_by_category_ids=${tagIds.join(',')}`);
            const employeesFromTags = response.data.employees;

            if (!employeesFromTags || employeesFromTags.length === 0) {
                notify("Aucun employé trouvé avec les étiquettes sélectionnées.", '', 1);
                return;
            }
            const employeesMap = new Map(employeesPrimes.map(ep => [ep.id_employee, ep]));
            const newEmployeesMap = new Map(newEmployeesPrimes.map(ep => [ep.id_employee, ep]));
            const modifiedEmployeesMap = new Map(modifiedEmployeesPrimes.map(ep => [ep.id_employee, ep]));

            employeesFromTags.forEach(emp => {
                const employeePrimeData = {
                    id_employee: emp.id,
                    employee_name: emp.name,
                    prime: amount,
                };

                const existingEmployee = employeesMap.get(emp.id);

                if (existingEmployee) {
                    employeePrimeData.id = existingEmployee.id;
                    if (!existingEmployee.isNew) {                        
                        modifiedEmployeesMap.set(emp.id, employeePrimeData);
                    } else {                        
                        newEmployeesMap.set(emp.id, employeePrimeData);
                    }
                } else {                    
                    employeePrimeData.isNew = true;
                    newEmployeesMap.set(emp.id, employeePrimeData);
                }                                
                employeesMap.set(emp.id, employeePrimeData);
            });            
            setEmployeesPrimes(Array.from(employeesMap.values()));
            setNewEmployeesPrimes(Array.from(newEmployeesMap.values()));
            setModifiedEmployeesPrimes(Array.from(modifiedEmployeesMap.values()));
            notify(`${employeesFromTags.length} employé(s) ajouté(s) grâce aux étiquettes.`, '', 2);
        } catch (error) {
            notify("Erreur lors de la récupération des employés par étiquette.", '', 3);
        }
    };

    const handlePageChange = (newOffset) => {
        const hasChanges = newEmployeesPrimes.length > 0 || modifiedEmployeesPrimes.length > 0 || removedEmployeesPrimes.length > 0;
        if (hasChanges) {
            setNextPageOffset(newOffset);
            setIsModalOpen(true);
        } else {
            setPagination(prev => ({ ...prev, offset: newOffset }));
        }
    };

    const proceedToNextPage = () => {
        if (nextPageOffset !== null) {
            setPagination(prev => ({ ...prev, offset: nextPageOffset }));
            setNextPageOffset(null);
        }
        setIsModalOpen(false);
    };

    const handleConfirmSaveChanges = async () => {
        if (saveChanges) {
            await saveChanges();
        }
        proceedToNextPage();
    };

    const handleDiscardChanges = () => {        
        setNewEmployeesPrimes([]);
        setModifiedEmployeesPrimes([]);
        setRemovedEmployeesPrimes([]);
        proceedToNextPage();
    };

    const handleAddNewLine = () => {
        const newEmployeePrime = {
            id_employee: '',
            employee_name: '',
            prime: '',
            isNew: true
        };
        setEmployeesPrimes(prev => [...prev, newEmployeePrime]);
        setNewEmployeesPrimes(prev => [...prev, newEmployeePrime]);
    };

    const handleEmployeeChange = (index, option) => {
        if (!option) {
            // Supprimer la ligne si aucun employé sélectionné
            handleRemove(index);
            return;
        }
        const updatedEmployeesPrimes = [...employeesPrimes];
        updatedEmployeesPrimes[index].id_employee = parseInt(option.value);
        updatedEmployeesPrimes[index].employee_name = option.label || '';
        setEmployeesPrimes(updatedEmployeesPrimes);
        setModifiedEmployeesPrimes(prev => {
            const filtered = prev.filter(item => item !== employeesPrimes[index]);
            return [...filtered, updatedEmployeesPrimes[index]];
        });
    };

    const handlePrimeChange = (index, value) => {
        const updatedEmployeesPrimes = [...employeesPrimes];
        updatedEmployeesPrimes[index].prime = value ? parseFloat(value) : '';
        setEmployeesPrimes(updatedEmployeesPrimes);        
         setModifiedEmployeesPrimes(prev => {
            const existing = prev.find(item => item.id_employee === updatedEmployeesPrimes[index].id_employee);
            if (existing) {
                return prev;
            }
            return [...prev, updatedEmployeesPrimes[index]];
        });
    };

    const handleRemove = (index) => {
        const employeeToRemove = employeesPrimes[index];        
        if (employeeToRemove.isNew) {        
            setNewEmployeesPrimes(prev => prev.filter(emp => emp !== employeeToRemove));
        } else {            
            setRemovedEmployeesPrimes(prev => [...prev, employeeToRemove]);
        }
        setEmployeesPrimes(prev => {
            const updated = [...prev];
            updated.splice(index, 1);
            return updated;
        });
    };

    return (
        <div className="space-y-4">
            <Modal
                isOpen={isModalOpen}
                onClose={() => setIsModalOpen(false)}
                title="Modifications non enregistrées"
                footer={
                    <>
                        <button type="button" onClick={() => setIsModalOpen(false)} className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors duration-200">
                            Annuler
                        </button>
                        <button type="button" onClick={handleDiscardChanges} className="px-4 py-2 text-orange-700 bg-orange-100 hover:bg-orange-200 rounded-lg transition-colors duration-200">
                            Ignorer et continuer
                        </button>
                        <button type="button" onClick={handleConfirmSaveChanges} className="px-4 py-2 bg-indigo-600 text-white hover:bg-indigo-700 rounded-lg transition-colors duration-200">
                            Enregistrer et continuer
                        </button>
                    </>
                }
            >
                <p className="text-gray-600">Vous avez des modifications non enregistrées. Voulez-vous les enregistrer avant de changer de page ?</p>
            </Modal>

            <Modal
                isOpen={isTagModalOpen}
                onClose={() => setIsTagModalOpen(false)}
                title={`Ajouter par Étiquette - ${type === "advantage" ? "Avantage" : "Prime"}`}
            >
                <TagModalContent 
                    onConfirm={handleAddByTag} 
                    onClose={() => setIsTagModalOpen(false)} 
                    type={type}
                />
            </Modal>

            {/* Actions Header */}
            <div className="flex justify-between items-center">
                <div className="text-sm text-gray-600">
                    {employeesPrimes.length > 0 && (
                        <span>{employeesPrimes.length} employé(s) assigné(s)</span>
                    )}
                </div>
                <div className="flex gap-3">
                    <button
                        type="button"
                        className="flex items-center gap-2 px-4 py-2 text-[--color-primary]  hover:bg-[--bg-color] rounded-lg transition-colors duration-200"
                        onClick={handleAddNewLine}
                    >
                        <FaPlus size={14} />
                        Ajouter un collaborateur
                    </button>
                    <button
                        type="button"
                        className="flex items-center gap-2 px-4 py-2 text-[--color-orange] hover:bg-orange-50 rounded-lg transition-colors duration-200"
                        onClick={() => setIsTagModalOpen(true)}
                    >
                        <FaTag size={14} />
                        Ajouter par étiquette
                    </button>
                </div>
            </div>

            {/* Table */}
            <div className="bg-white rounded-lg border border-gray-200 overflow-visible">
                <div className="overflow-x-auto">
                    <table className="w-full">
                        <thead className="bg-gray-50 border-b border-gray-200">
                            <tr>
                                <th className="px-6 py-4 text-left text-sm font-medium text-gray-700">Employé</th>
                                <th className="px-6 py-4 text-left text-sm font-medium text-gray-700">Montant</th>
                                <th className="px-6 py-4 text-center text-sm font-medium text-gray-700">Actions</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200">
                            {employeesPrimes.length === 0 ? (
                                <tr>
                                    <td colSpan="3" className="px-6 py-12 text-center text-gray-500">
                                        <div className="flex flex-col items-center">
                                            <div className="bg-gray-100 rounded-full p-3 mb-3">
                                                <FaPlus className="text-gray-400" size={24} />
                                            </div>
                                            <p className="text-lg font-medium mb-1">Aucun employé assigné</p>
                                            <p className="text-sm">Cliquez sur "Ajouter un collaborateur" pour commencer</p>
                                        </div>
                                    </td>
                                </tr>
                            ) : (
                                employeesPrimes.map((emp, index) => {
                                    const emp_select = emp.id_employee ? { value: emp.id_employee, label: emp.employee_name } : null;
                                    return (
                                        <tr key={index} className="hover:bg-gray-50 transition-colors duration-200">
                                            <td className="px-6 py-4 relative">
                                                <EmployeeSelect
                                                    value={emp_select}
                                                    onChange={(option) => handleEmployeeChange(index, option)}
                                                    isDisabled={false}
                                                    placeholder="Sélectionner un collaborateur"
                                                    className="min-w-0"
                                                    menuPortalTarget={document.body}
                                                    styles={{
                                                        menuPortal: (base) => ({ ...base, zIndex: 9999 })
                                                    }}
                                                />
                                            </td>                                    
                                            <td className="px-6 py-4">
                                                <div className="flex items-center gap-2">
                                                    <AmountInput
                                                        value={emp.prime}
                                                        onChange={(newValue) => handlePrimeChange(index, newValue)}
                                                        className="w-32 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-colors duration-200"
                                                    />
                                                </div>
                                            </td>
                                            <td className="px-6 py-4 text-center">
                                                <button
                                                    type="button"
                                                    onClick={() => handleRemove(index)}
                                                    className="inline-flex items-center justify-center w-8 h-8 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-full transition-colors duration-200"
                                                    title="Supprimer cette ligne"
                                                >
                                                    <FaTrashAlt size={14} />
                                                </button>
                                            </td>
                                        </tr>
                                    );
                                })
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Error Message */}
            {errorMessage && (
                <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                    <p className="text-red-800 text-sm">{errorMessage}</p>
                </div>
            )}

            {/* Pagination */}
            {pagination.total_pages > 1 && (
                <div className="flex justify-between items-center pt-4">
                    <div className="text-sm text-gray-600">
                        Affichage {pagination.offset + 1} à {Math.min(pagination.offset + pagination.limit, pagination.total_count)} sur {pagination.total_count} résultats
                    </div>
                    <div className="flex items-center gap-2">
                        <button
                            type="button"
                            className="px-3 py-2 text-gray-600 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
                            disabled={pagination.offset === 0}
                            onClick={() => handlePageChange(pagination.offset - pagination.limit)}
                        >
                            Précédent
                        </button>
                        <span className="px-3 py-2 text-sm text-gray-700">
                            Page {Math.floor(pagination.offset / pagination.limit) + 1} sur {pagination.total_pages}
                        </span>
                        <button
                            type="button"
                            className="px-3 py-2 text-gray-600 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
                            disabled={pagination.offset + pagination.limit >= pagination.total_count}
                            onClick={() => handlePageChange(pagination.offset + pagination.limit)}
                        >
                            Suivant
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default EmployeesPrime;